import { createQueryClient } from './toast-handler';

// Create separate query client instances for user and admin contexts
// This prevents cache contamination between contexts
// Using in-memory cache only - httpOnly cookies provide the isolation
export const userQueryClient = createQueryClient();
export const adminQueryClient = createQueryClient();

// Default export for backward compatibility
export const queryClient = userQueryClient;

/**
 * Query key factory for consistent query key generation
 * This helps with cache invalidation and query organization
 */
export const queryKeys = {
  all: ['coldsea-deploy'] as const,

  // Workflows
  workflows: () => [...queryKeys.all, 'workflows'] as const,
  workflowsList: (orgId: string) => [...queryKeys.workflows(), 'list', orgId] as const,
  workflowDetail: (id: string) => [...queryKeys.workflows(), 'detail', id] as const,

  // Users
  users: () => [...queryKeys.all, 'users'] as const,
  usersList: (orgId: string) => [...queryKeys.users(), 'list', orgId] as const,
  userDetail: (id: string) => [...queryKeys.users(), 'detail', id] as const,
  userProfile: () => [...queryKeys.users(), 'profile'] as const,
  userPreferences: () => [...queryKeys.users(), 'preferences'] as const,

  // MFA status (current user's enrollment + recovery-codes-remaining)
  mfaStatus: () => [...queryKeys.all, 'mfa-status'] as const,

  // Organizations
  organizations: () => [...queryKeys.all, 'organizations'] as const,
  organizationsList: () => [...queryKeys.organizations(), 'list'] as const,
  organizationDetail: (id: string) => [...queryKeys.organizations(), 'detail', id] as const,
  organizationsWithDefault: () => [...queryKeys.organizations(), 'with-default'] as const,
  pendingInvitations: () => [...queryKeys.all, 'pending-invitations'] as const,
  pendingMembershipRequests: () => [...queryKeys.all, 'pending-membership-requests'] as const,

  // Executions
  executions: () => [...queryKeys.all, 'executions'] as const,
  executionsList: (orgId: string) => [...queryKeys.executions(), 'list', orgId] as const,
  executionDetail: (id: string) => [...queryKeys.executions(), 'detail', id] as const,

  // Components
  components: () => [...queryKeys.all, 'components'] as const,
  componentsList: (orgId: string, status?: string, summary?: boolean) =>
    [...queryKeys.components(), 'list', orgId, status, summary] as const,
  componentDetail: (id: string) => [...queryKeys.components(), 'detail', id] as const,

  // Action Templates
  actionTemplates: () => [...queryKeys.all, 'action-templates'] as const,
  actionTemplatesList: () => [...queryKeys.actionTemplates(), 'list'] as const,

  // Hosts
  hosts: () => [...queryKeys.all, 'hosts'] as const,
  hostsList: (orgId: string) => [...queryKeys.hosts(), 'list', orgId] as const,
  hostDetail: (id: string) => [...queryKeys.hosts(), 'detail', id] as const,

  // Permissions
  permissions: () => [...queryKeys.all, 'permissions'] as const,
  permissionsList: () => [...queryKeys.permissions(), 'list'] as const,

  // Roles
  roles: () => [...queryKeys.all, 'roles'] as const,
  rolesList: () => [...queryKeys.roles(), 'list'] as const,
  rolePermissions: (roleId: string) => [...queryKeys.roles(), 'permissions', roleId] as const,

  // Configuration
  config: {
    capabilities: ['config', 'capabilities'] as const,
  },

  // Subscriptions
  subscriptions: () => [...queryKeys.all, 'subscriptions'] as const,
  subscriptionCurrent: (orgId?: string) =>
    [...queryKeys.subscriptions(), 'current', orgId] as const,
  subscriptionUsage: () => [...queryKeys.subscriptions(), 'usage'] as const,
  subscriptionTiers: () => [...queryKeys.subscriptions(), 'tiers'] as const,

  // Storage
  storage: () => [...queryKeys.all, 'storage'] as const,
  storageFiles: (orgId: string, folderId?: string | null) =>
    [...queryKeys.storage(), 'files', orgId, folderId ?? 'root'] as const,
  storageQuota: (orgId: string) => [...queryKeys.storage(), 'quota', orgId] as const,

  // Webhooks
  webhooks: () => [...queryKeys.all, 'webhooks'] as const,
  webhooksList: (orgId: string) => [...queryKeys.webhooks(), 'list', orgId] as const,
  webhookDetail: (id: string) => [...queryKeys.webhooks(), 'detail', id] as const,
  webhookDeliveries: (id: string) => [...queryKeys.webhooks(), id, 'deliveries'] as const,
  webhooksAvailableEvents: () => [...queryKeys.webhooks(), 'available-events'] as const,

  // Notifications
  notifications: () => [...queryKeys.all, 'notifications'] as const,
  notificationInbox: () => [...queryKeys.notifications(), 'inbox'] as const,
  notificationUnreadCount: () => [...queryKeys.notifications(), 'unread-count'] as const,
  notificationLoginAnnouncements: () =>
    [...queryKeys.notifications(), 'login-announcements'] as const,

  // Task Guide
  taskGuide: () => [...queryKeys.all, 'task-guide'] as const,
  taskGuideTasks: (orgId?: string) => [...queryKeys.taskGuide(), 'tasks', orgId] as const,
} as const;
